import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Drawer, AppBar, Toolbar, Typography,
  List, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Avatar, Divider, Tooltip, Badge,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
  Popover, CircularProgress, Fade,
} from "@mui/material";
import {
  LayoutDashboard, BookOpen, Users, Inbox,
  FileText, Settings, Menu as MenuIcon, X,
  ScrollText, CalendarDays, LogOut, Map, Briefcase, Globe, BarChart3, Cpu,
  Sparkles, ExternalLink, Bell, ChevronRight,
  Package, Building2, MessageSquare, Layers,
  UserPlus, Mail, GraduationCap, Info, CheckCheck,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { logout } from "../../api/auth";
// Adjust this import to match your project's shared API client — an axios
// instance (or similar) that already attaches the Authorization: Bearer token,
// the same way "../../api/auth" does for the logout call above.
import apiClient from "../../api/axios";
import toast from "react-hot-toast";

const DRAWER_WIDTH = 264;

const navGroups = [
  {
    label: "Overview",
    items: [
      { label: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },
      { label: "Metrics",   icon: BarChart3,        path: "/admin/metrics" },
    ],
  },
  {
    label: "Content",
    items: [
      { label: "Courses",        icon: BookOpen,  path: "/admin/courses" },
         { label: "Course Category", icon: BookOpen, path: "/admin/courses-category" },
      { label: "Training",       icon: GraduationCap,  path: "/admin/trainings" },      // ← add
      { label: "Testimonials",   icon: MessageSquare,  path: "/admin/testimonials" },
      { label: "Learning Paths", icon: Map,       path: "/admin/learning-paths" },
      { label: "Service Category",       icon: Briefcase, path: "/admin/services-category" },
      { label: "Services",       icon: Briefcase, path: "/admin/services" },
      { label: "Resources",      icon: FileText,  path: "/admin/resources" },
      { label: "Resources Category",      icon: FileText,  path: "/admin/resources-category" },
      { label: "Pages",          icon: Globe,     path: "/admin/pages" },
      { label: "Tech Stack",     icon: Cpu,       path: "/admin/tech-stack" },
    ],
  },
 {
    label: "Business",
    items: [
      { label: "Business Offerings", icon: Briefcase,     path: "/admin/business-offerings" },
      { label: "Product Categories", icon: Layers,        path: "/admin/product-categories" },
      { label: "Vendors",            icon: Building2,     path: "/admin/vendors" },
      { label: "Products",           icon: Package,       path: "/admin/products" },
      { label: "Partners & Clients", icon: Users,         path: "/admin/partners" },
      // { label: "Business Inquiries", icon: MessageSquare, path: "/admin/business-inquiries" },
      { label: "Page Content",       icon: Globe,         path: "/admin/business-page-content" },
    ],
  },
  {
    label: "Engagement",
    items: [
      { label: "Leads",  icon: Inbox,        path: "/admin/leads" },
      { label: "Events", icon: CalendarDays, path: "/admin/events" },
    ],
  },
  {
    label: "System",
    items: [
      { label: "Users",      icon: Users,      path: "/admin/users" },
      { label: "Settings",   icon: Settings,   path: "/admin/settings" },
      { label: "Navbar Settings",   icon: Settings,   path: "/admin/navbar-settings" },
      { label: "Site Configuration",   icon: Settings,   path: "/admin/site-config" },
      { label: "Audit Logs", icon: ScrollText, path: "/admin/audit-logs" },
    ],
  },
];

// flat lookup, used to find a nav item's icon/group for the top-bar breadcrumb
const flatNavItems = navGroups.flatMap((g) => g.items.map((item) => ({ ...item, group: g.label })));

// --- Notifications: types, styling, helpers -------------------------------

const POLL_INTERVAL_MS = 30000;

// Visual identity per notification type — colored circular icon badges,
// Instagram/Facebook-style, rather than flat monochrome icons.
const TYPE_STYLES = {
  GENERAL_LEAD: { icon: UserPlus, bg: "#EFF6FF", color: "#2563EB" },
  CONTACT_LEAD: { icon: Mail, bg: "#ECFDF5", color: "#059669" },
  APPLICATION: { icon: Briefcase, bg: "#FFF7ED", color: "#EA580C" },
  TRAINING_LEAD: { icon: GraduationCap, bg: "#FAF5FF", color: "#9333EA" },
};
const DEFAULT_TYPE_STYLE = { icon: Info, bg: "#F1F5F9", color: "#475569" };

function timeAgo(isoString) {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffSec = Math.max(0, Math.floor(diffMs / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  const diffWeek = Math.floor(diffDay / 7);
  return `${diffWeek}w`;
}

function SidebarContent({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const handleLogoutConfirm = async () => {
    setLogoutOpen(false);
    try {
      await logout();
    } catch {}
    signOut();
    navigate("/admin/login");
    toast.success("Logged out");
  };

  return (
    <Box sx={{ position: "relative", height: "100%", bgcolor: "#0A0F1E", color: "white", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* ambient glow */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -120,
          left: -60,
          width: 280,
          height: 280,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(37,99,235,0.22) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Logo */}
      <Box sx={{ position: "relative", p: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 8px 20px -8px rgba(37,99,235,0.6)",
            }}
          >
            <Sparkles size={17} color="white" strokeWidth={2.2} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.45)", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", lineHeight: 1.3 }}>
              Admin Panel
            </Typography>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "white", lineHeight: 1.3 }}>
              NetLabs ITS
            </Typography>
          </Box>
        </Box>
        {onClose && (
          <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.5)" }}>
            <X size={18} />
          </IconButton>
        )}
      </Box>

      {/* View live site */}
      <Box sx={{ position: "relative", px: 2.5, pb: 1.5 }}>
        <Box
          component="a"
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 12,
            fontWeight: 600,
            color: "rgba(255,255,255,0.5)",
            textDecoration: "none",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: "9px",
            px: 1.5,
            py: 0.85,
            transition: "all 0.18s ease",
            "&:hover": { color: "white", borderColor: "rgba(255,255,255,0.18)", bgcolor: "rgba(255,255,255,0.04)" },
          }}
        >
          View live site
          <ExternalLink size={13} strokeWidth={2.2} />
        </Box>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* Nav */}
      <List
        sx={{
          position: "relative",
          px: 1.5,
          py: 1.25,
          flexGrow: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.12)", borderRadius: 3 },
        }}
      >
        {navGroups.map((group, gi) => (
          <Box key={group.label} sx={{ mb: gi === navGroups.length - 1 ? 0 : 1.75 }}>
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.32)",
                px: 1.5,
                mb: 0.75,
              }}
            >
              {group.label}
            </Typography>
            {group.items.map(({ label, icon: Icon, path }) => {
              const active = location.pathname === path;
              return (
                <ListItemButton
                  key={label}
                  onClick={() => { navigate(path); onClose?.(); }}
                  sx={{
                    position: "relative",
                    borderRadius: "10px",
                    mb: 0.4,
                    py: 1,
                    pl: active ? 1.75 : 1.5,
                    bgcolor: active ? "rgba(37,99,235,0.16)" : "transparent",
                    color: active ? "#60A5FA" : "rgba(255,255,255,0.6)",
                    transition: "background-color 0.18s ease, color 0.18s ease, padding-left 0.18s ease",
                    "&:hover": { bgcolor: active ? "rgba(37,99,235,0.2)" : "rgba(255,255,255,0.06)", color: active ? "#60A5FA" : "white" },
                    "&::before": active
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "18%",
                          bottom: "18%",
                          width: "3px",
                          borderRadius: "0 4px 4px 0",
                          bgcolor: "#2563EB",
                        }
                      : {},
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: "inherit" }}>
                    <Icon size={17} strokeWidth={2.1} />
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    slotProps={{ primary: { sx: { fontSize: 13.75, fontWeight: active ? 700 : 600 } } }}
                  />
                </ListItemButton>
              );
            })}
          </Box>
        ))}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* User footer */}
      <Box sx={{ position: "relative", p: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            p: 1.25,
            borderRadius: "12px",
            bgcolor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.07)",
          }}
        >
          <Avatar
            sx={{
              background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
              width: 36,
              height: 36,
              fontSize: 13.5,
              fontWeight: 700,
            }}
          >
            {user?.full_name?.[0] ?? "A"}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} color="white" noWrap sx={{ fontSize: 13.5, lineHeight: 1.3 }}>
              {user?.full_name}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 11.5, textTransform: "capitalize" }} noWrap>
              {user?.role}
            </Typography>
          </Box>
          <Tooltip title="Logout">
            <IconButton
              onClick={() => setLogoutOpen(true)}
              size="small"
              sx={{
                color: "rgba(255,255,255,0.45)",
                borderRadius: "8px",
                "&:hover": { color: "#F87171", bgcolor: "rgba(248,113,113,0.1)" },
              }}
            >
              <LogOut size={16} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography sx={{ textAlign: "center", fontSize: 10.5, color: "rgba(255,255,255,0.25)", mt: 1.25 }}>
          NetLabs ITS Admin · v1.0
        </Typography>
      </Box>

      {/* Logout confirmation dialog */}
      <Dialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 17 }}>
          Log out?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            You'll need to sign in again to access the admin panel.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setLogoutOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 700, boxShadow: "none" }}
          >
            Log out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- Notification bell: badge + dropdown, wired to the admin API ----------

function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  // Tracks which individual notification's "mark as read" request is in
  // flight, so we can show a small spinner on just that row's icon.
  const [markingIds, setMarkingIds] = useState(() => new Set());
  const pollRef = useRef(null);

  const open = Boolean(anchorEl);
  const unreadCount = notifications.length;

  const loadNotifications = useCallback(async () => {
    try {
      setError(false);
      const { data } = await apiClient.get("/api/admin/notifications/unread/");
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + background polling so the badge count stays fresh even
  // while the dropdown is closed.
  useEffect(() => {
    loadNotifications();
    pollRef.current = setInterval(loadNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [loadNotifications]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    loadNotifications();
  };
  const handleClose = () => setAnchorEl(null);

  // Marking a notification as read is now a deliberate action triggered by
  // the small "X" icon on each row, not by clicking anywhere on the row.
  const handleMarkAsRead = async (e, notification) => {
    e.stopPropagation();
    setMarkingIds((prev) => new Set(prev).add(notification.id));
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    try {
      await apiClient.post(`/api/admin/notifications/${notification.id}/read/`);
    } catch (err) {
      setNotifications((prev) => [notification, ...prev]);
    } finally {
      setMarkingIds((prev) => {
        const next = new Set(prev);
        next.delete(notification.id);
        return next;
      });
    }
  };

  const handleMarkAllRead = async () => {
    if (!notifications.length) return;
    const ids = notifications.map((n) => n.id);
    const previous = notifications;
    setMarkingAll(true);
    setNotifications([]);
    try {
      await Promise.all(ids.map((id) => apiClient.post(`/api/admin/notifications/${id}/read/`)));
    } catch (err) {
      setNotifications(previous);
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "9px" }} onClick={handleOpen}>
          <Badge
            badgeContent={unreadCount}
            max={9}
            sx={{
              "& .MuiBadge-badge": {
                bgcolor: "#EF4444",
                color: "white",
                fontSize: 10.5,
                fontWeight: 700,
                minWidth: 17,
                height: 17,
                border: "2px solid white",
              },
            }}
          >
            <Bell size={18} color="#334155" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: "calc(100vw - 24px)",
            mt: 1.25,
            borderRadius: "16px",
            boxShadow: "0 20px 48px -12px rgba(15,23,42,0.22)",
            border: "1px solid rgba(15,23,42,0.06)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2.25, py: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15.5, color: "#0F172A" }}>Notifications</Typography>
          {unreadCount > 0 && (
            <Box
              component="button"
              onClick={handleMarkAllRead}
              disabled={markingAll}
              sx={{
                all: "unset",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: markingAll ? "default" : "pointer",
                fontSize: 12.5,
                fontWeight: 700,
                color: "#2563EB",
                opacity: markingAll ? 0.5 : 1,
                "&:hover": { color: markingAll ? "#2563EB" : "#1D4ED8" },
              }}
            >
              <CheckCheck size={14} />
              Mark all as read
            </Box>
          )}
        </Box>
        <Divider />

        {/* Body */}
        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={22} thickness={4} sx={{ color: "#2563EB" }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ textAlign: "center", py: 5, px: 3 }}>
              <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
                Couldn't load notifications. Pull down to try again.
              </Typography>
            </Box>
          )}

          {!loading && !error && notifications.length === 0 && (
            <Box sx={{ textAlign: "center", py: 6, px: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  bgcolor: "#F1F5F9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <Bell size={20} color="#94A3B8" />
              </Box>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>You're all caught up</Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
                No new notifications right now.
              </Typography>
            </Box>
          )}

          {!loading &&
            !error &&
            notifications.map((n) => {
              const style = TYPE_STYLES[n.type] || DEFAULT_TYPE_STYLE;
              const Icon = style.icon;
              const isMarking = markingIds.has(n.id);
              return (
                <Box
                  key={n.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: 2.25,
                    py: 1.5,
                    position: "relative",
                    bgcolor: "rgba(37,99,235,0.035)",
                    transition: "background-color 0.15s ease",
                    "&:hover": { bgcolor: "rgba(37,99,235,0.07)" },
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "50%",
                      bgcolor: style.bg,
                      color: style.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={17} strokeWidth={2.2} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: "#0F172A",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {n.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        color: "text.secondary",
                        lineHeight: 1.5,
                        mt: 0.25,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {n.message}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#94A3B8", mt: 0.5, fontWeight: 600 }}>
                      {timeAgo(n.created_at)}
                    </Typography>
                  </Box>

                  {/* Mark-as-read action — a dedicated "cut" (X) icon instead
                      of the whole row being clickable. */}
                  <Tooltip title="Mark as read">
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMarkAsRead(e, n)}
                        disabled={isMarking}
                        sx={{
                          flexShrink: 0,
                          mt: 0.25,
                          width: 26,
                          height: 26,
                          color: "#94A3B8",
                          border: "1px solid rgba(15,23,42,0.08)",
                          bgcolor: "white",
                          "&:hover": {
                            color: "#EF4444",
                            borderColor: "rgba(239,68,68,0.35)",
                            bgcolor: "rgba(239,68,68,0.06)",
                          },
                        }}
                      >
                        {isMarking ? (
                          <CircularProgress size={13} thickness={5} sx={{ color: "inherit" }} />
                        ) : (
                          <X size={14} strokeWidth={2.4} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
        </Box>
      </Popover>
    </>
  );
}

export default function AdminShell({ children, title }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  const currentItem = flatNavItems.find((item) => item.path === location.pathname);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#F8FAFC" }}>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, border: "none" },
        }}
      >
        <SidebarContent onClose={() => setMobileOpen(false)} />
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "flex" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, border: "none", boxSizing: "border-box" },
        }}
        open
      >
        <SidebarContent />
      </Drawer>

      {/* Main content */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0, ml: { lg: `${DRAWER_WIDTH}px` } }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{ bgcolor: "rgba(255,255,255,0.86)", backdropFilter: "blur(12px)", borderBottom: "1px solid rgba(0,0,0,0.06)", top: 0 }}
        >
          <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, gap: 1.5 }}>
            <IconButton
              sx={{ display: { lg: "none" }, border: "1px solid rgba(15,23,42,0.1)", borderRadius: "9px" }}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon size={20} />
            </IconButton>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {currentItem && (
                <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.5, mb: 0.25 }}>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 600 }}>
                    {currentItem.group}
                  </Typography>
                  <ChevronRight size={13} color="#94A3B8" />
                  <Typography sx={{ fontSize: 12, color: "#2563EB", fontWeight: 700 }}>
                    {currentItem.label}
                  </Typography>
                </Box>
              )}
              <Typography
                variant="h6"
                fontWeight={700}
                color="text.primary"
                noWrap
                sx={{ fontSize: { xs: 17, md: 19 }, letterSpacing: "-0.01em" }}
              >
                {title}
              </Typography>
            </Box>

            <NotificationBell />

            <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" }, mx: 0.5, my: 1.25 }} />

            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1 }}>
              <Avatar
                sx={{
                  background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                  width: 34,
                  height: 34,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {user?.full_name?.[0] ?? "A"}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                  {user?.full_name ?? "Admin"}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "capitalize", lineHeight: 1.3 }} noWrap>
                  {user?.role ?? "Administrator"}
                </Typography>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}